import { useState, useEffect } from 'react';
import { apiService } from '@/lib/api-functions/api-service';

/**
 * Hook to monitor token refresh status
 * Useful for debugging and displaying token expiration warnings
 */
export function useTokenRefresh() {
  const [tokenInfo, setTokenInfo] = useState<ReturnType<typeof apiService.getTokenInfo>>(null);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);

  useEffect(() => {
    // Update token info every minute
    const updateTokenInfo = () => {
      const info = apiService.getTokenInfo();
      setTokenInfo(info);
    };

    // Initial update
    updateTokenInfo();

    // Update every minute
    const interval = setInterval(updateTokenInfo, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Detect when token has been refreshed by watching for expiration time changes
  useEffect(() => {
    if (tokenInfo && !tokenInfo.isExpired) {
      const currentExpiration = tokenInfo.timeUntilExpiration;
      
      // If expiration time suddenly increased, token was refreshed
      const previousExpiration = sessionStorage.getItem('last_token_expiration');
      if (previousExpiration) {
        const prev = parseInt(previousExpiration, 10);
        // Parse time strings like "23h 45m" to minutes
        const currentMinutes = parseTimeToMinutes(currentExpiration);
        const prevMinutes = parseTimeToMinutes(previousExpiration);
        
        // If current expiration is significantly more than previous (e.g., went from 1h to 23h)
        if (currentMinutes > prevMinutes + 60) { // More than 60 minutes increase
          setLastRefreshTime(new Date());
          console.log('TokenRefresh: Token was refreshed at', new Date().toLocaleString());
        }
      }
      
      sessionStorage.setItem('last_token_expiration', currentExpiration);
    }
  }, [tokenInfo?.timeUntilExpiration]);

  const manualRefresh = async () => {
    try {
      await apiService.manualRefresh();
      setLastRefreshTime(new Date());
      
      // Update token info immediately after refresh
      const info = apiService.getTokenInfo();
      setTokenInfo(info);
    } catch (error) {
      console.error('Manual token refresh failed:', error);
      throw error;
    }
  };

  return {
    tokenInfo,
    lastRefreshTime,
    manualRefresh,
  };
}

/**
 * Parse time strings like "23h 45m" or "30m 15s" to total minutes
 */
function parseTimeToMinutes(timeStr: string): number {
  const hourMatch = timeStr.match(/(\d+)h/);
  const minuteMatch = timeStr.match(/(\d+)m/);
  
  const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
  const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;
  
  return hours * 60 + minutes;
}
